import { type UseInfiniteQueryOptions, type UseQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  type SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetData,
  type SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetError,
  type SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetResponse,
  searchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGet,
} from '../../generated';

export type SearchCollectionsQueryVariables =
  SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetData['path'] &
    SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetData['query'];

export type SearchCollectionsQuery = {
  data: SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchCollectionsQuery = <
  TData = SearchCollectionsQuery,
  TError = SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetError,
>(
  { portfolio_id, sort_by, limit, offset, sort_direction }: SearchCollectionsQueryVariables,
  options?: Omit<UseQueryOptions<SearchCollectionsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchCollectionsQuery, TError, TData>({
    queryKey: ['SearchCollections', { portfolio_id, sort_by, limit, offset, sort_direction }],
    queryFn: async () => {
      const { data, response } = await searchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGet({
        path: { portfolio_id },
        query: { sort_by, limit, offset, sort_direction },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchCollectionsQuery.getKey = ({
  portfolio_id,
  sort_by,
  limit,
  offset,
  sort_direction,
}: SearchCollectionsQueryVariables) => ['SearchCollections', { portfolio_id, sort_by, limit, offset, sort_direction }];

useSearchCollectionsQuery.fetcher = (
  { portfolio_id, sort_by, limit, offset, sort_direction }: SearchCollectionsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGet({
      path: { portfolio_id },
      query: { sort_by, limit, offset, sort_direction },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchCollectionsInfiniteQueryVariables = Omit<SearchCollectionsQueryVariables, 'offset'>;

export const useSearchCollectionsInfiniteQuery = <
  TData = SearchCollectionsQuery,
  TError = SearchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGetError,
>(
  { portfolio_id, sort_by, limit, sort_direction }: SearchCollectionsInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchCollectionsQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchCollectionsQuery, TError, TData>({
    queryKey: [
      'SearchCollectionsInfinite',
      {
        portfolio_id,
        sort_by,
        limit,
        sort_direction,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchCollectionsV1PortfoliosPortfolioIdAssetsImagesCollectionsGet({
        path: { portfolio_id },
        query: {
          sort_by,
          limit,
          offset: pageParam as number,
          sort_direction,
        },
        throwOnError: true,
      });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (!lastPage?.data || lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    ...options,
  });
};
